/**
 * Utility for copying text to clipboard with fallback support
 * Handles environments where the Clipboard API is blocked
 */

/**
 * Show a toast notification for copy feedback
 * This is a simple implementation - in a real app you might use a toast library
 */
function showToast(message: string, type: 'success' | 'error' = 'success'): void {
  // Create toast element
  const toast = document.createElement('div')
  toast.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: ${type === 'success' ? '#10b981' : '#ef4444'};
    color: white;
    padding: 12px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 10001;
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 14px;
    max-width: 300px;
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;
  `
  toast.textContent = message
  document.body.appendChild(toast)

  // Animate in
  setTimeout(() => {
    toast.style.transform = 'translateX(0)'
  }, 10)

  // Remove after delay
  setTimeout(() => {
    toast.style.transform = 'translateX(100%)'
    setTimeout(() => {
      if (document.body.contains(toast)) {
        document.body.removeChild(toast)
      }
    }, 300)
  }, 3000)
}

export async function copyToClipboard(text: string, showNotification: boolean = true): Promise<boolean> {
  // First try the modern Clipboard API
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text)
      if (showNotification) {
        showToast('Copied to clipboard!')
      }
      return true
    } catch (error) {
      console.warn('Clipboard API failed, falling back to execCommand:', error)
    }
  }

  // Fallback to the older execCommand method
  try {
    // Create a temporary textarea element
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    textArea.style.top = '-999999px'
    textArea.setAttribute('readonly', '')
    textArea.style.opacity = '0'
    
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    
    // Try to copy using execCommand
    const result = document.execCommand('copy')
    document.body.removeChild(textArea)
    
    if (result) {
      if (showNotification) {
        showToast('Copied to clipboard!')
      }
      return true
    }
  } catch (error) {
    console.error('execCommand copy failed:', error)
  }

  // If all methods fail, show appropriate feedback
  if (showNotification) {
    showToast('Copy failed. Please copy manually.', 'error')
  }
  
  // Show a manual copy prompt as last resort
  showManualCopyPrompt(text)
  return false
}

/**
 * Show a prompt with the text for manual copying
 */
function showManualCopyPrompt(text: string): void {
  // Create a modal-like prompt for manual copying
  const modal = document.createElement('div')
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    font-family: system-ui, -apple-system, sans-serif;
  `

  const content = document.createElement('div')
  content.style.cssText = `
    background: white;
    padding: 20px;
    border-radius: 8px;
    max-width: 400px;
    margin: 20px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  `

  content.innerHTML = `
    <h3 style="margin: 0 0 15px 0; font-size: 18px; font-weight: 600;">Copy to Clipboard</h3>
    <p style="margin: 0 0 15px 0; color: #666; font-size: 14px;">Please manually copy the text below:</p>
    <div style="
      background: #f5f5f5;
      padding: 10px;
      border-radius: 4px;
      font-family: monospace;
      font-size: 14px;
      word-break: break-all;
      margin-bottom: 15px;
      border: 1px solid #ddd;
    ">${text}</div>
    <div style="display: flex; gap: 10px; justify-content: flex-end;">
      <button id="select-text" style="
        background: #007bff;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
      ">Select Text</button>
      <button id="close-modal" style="
        background: #6c757d;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
      ">Close</button>
    </div>
  `

  modal.appendChild(content)
  document.body.appendChild(modal)

  // Add event listeners
  const selectButton = content.querySelector('#select-text') as HTMLButtonElement
  const closeButton = content.querySelector('#close-modal') as HTMLButtonElement
  const textElement = content.querySelector('div[style*="font-family: monospace"]') as HTMLDivElement

  selectButton.addEventListener('click', () => {
    const range = document.createRange()
    range.selectNodeContents(textElement)
    const selection = window.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)
  })

  closeButton.addEventListener('click', () => {
    document.body.removeChild(modal)
  })

  // Close on backdrop click
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      document.body.removeChild(modal)
    }
  })

  // Close on escape key
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      document.body.removeChild(modal)
      document.removeEventListener('keydown', handleEscape)
    }
  }
  document.addEventListener('keydown', handleEscape)
}

/**
 * Copy text with user feedback
 * Returns a promise that resolves to whether the copy was successful
 */
export async function copyWithFeedback(
  text: string,
  onSuccess?: () => void,
  onError?: (error: Error) => void
): Promise<boolean> {
  try {
    const success = await copyToClipboard(text)
    if (success && onSuccess) {
      onSuccess()
    }
    return success
  } catch (error) {
    if (onError) {
      onError(error as Error)
    }
    return false
  }
}